import * as fs from "fs";
import * as path from "path";
import { PanoramaClient, panoramaClientFactory, PanoramaPlatform, createGlobalConfig } from "@wix/panorama-client-node";
import { createRequire } from "module";

const DEFAULT_LOG_FILE = path.resolve(process.cwd(), ".wix/lsp/wix-code-lsp-server.log");

let requireFunc: NodeRequire;
try {
  // In ESM context
  requireFunc = createRequire(import.meta.url);
} catch {
  // In CommonJS context, use global require
  requireFunc = require;
}

const packageJson = requireFunc("../../package.json");
const artifactVersion = packageJson.version;


export type LogSeverity = 'info' | 'warn' | 'error' | 'debug';
export interface WritableLogger {
  log(severity: LogSeverity, message: string): void;
}

export class FileLogger implements WritableLogger {
  private logFile: string;

  constructor(logFile?: string) {
    this.logFile = logFile || DEFAULT_LOG_FILE;
    this._createLogDirIfNotExists();
  }

  log(severity: LogSeverity, message: string): void {
    try {
      const formattedMessage = this._formatMessage(severity, message);
      fs.appendFileSync(this.logFile, formattedMessage + "\n", { encoding: "utf8" });
    } catch (err) {
      console.error("Logger failed to write to log file:", err);
      console.error(message);
    }
  }

  private _createLogDirIfNotExists(): void {
    const logDir = path.dirname(this.logFile);
    if (!fs.existsSync(logDir)) {
      fs.mkdirSync(logDir, { recursive: true });
    }
  }

  private _formatMessage(severity: LogSeverity, message: string): string {
    return `${new Date().toISOString()} ${severity.toUpperCase()} ${message}`;
  }
}

export class PanoramaLogger implements WritableLogger {
  private panormaClient: PanoramaClient;

  constructor() {
    this.panormaClient = this._createPanoramaClient();
  }

  private _createPanoramaClient(): PanoramaClient {
    return panoramaClientFactory({
      baseParams: {
        platform: PanoramaPlatform.Standalone,
        fullArtifactId: "com.wixpress.wix-code-lsp-server",
        artifactVersion,
        componentId: "wix-code-lsp-server"
      }
    })
    .withGlobalConfig(createGlobalConfig())
    .client();
  }

  log(severity: LogSeverity, message: string): void {
    const panoramaLogger = this.panormaClient.logger();

    switch (severity) {
      case "info":
        panoramaLogger.info(message);
        break;
      case "warn":
        panoramaLogger.warn(message);
        break;
      case "error":
        panoramaLogger.error(message);
        break;
      case "debug":
        panoramaLogger.debug(message);
        break;
    }
  }
}

export class Logger {
  private targets: WritableLogger[];
  private debugEnabled: boolean;

  constructor({ targets, debugEnabled }: { targets: WritableLogger[], debugEnabled?: boolean }) {
    this.targets = targets;
    this.debugEnabled = debugEnabled ?? false;
  }

  private stringifyMessage(
    message: string,
    ...args: any[]
  ): string {
    let msg = message;
    if (args && args.length > 0) {
      msg +=
        " " +
        args
          .map((arg) => {
            try {
              return typeof arg === "string" ? arg : JSON.stringify(arg);
            } catch {
              return "[Unserializable]";
            }
          })
          .join(" ");
    }
    return msg;
  }

  info(message: string, ...args: any[]): void {
    const msg = this.stringifyMessage(message, ...args);
    this.targets.forEach((target) => target.log("info", msg));
  }

  warn(message: string, ...args: any[]): void {
    const msg = this.stringifyMessage(message, ...args);
    this.targets.forEach((target) => target.log("warn", msg));
  }

  error(message: string, ...args: any[]): void {
    const msg = this.stringifyMessage(message, ...args);
    this.targets.forEach((target) => target.log("error", msg));
  }

  debug(message: string, ...args: any[]): void {
    if (this.debugEnabled) {
      const msg = this.stringifyMessage(message, ...args);
      this.targets.forEach((target) => target.log("debug", msg));
    }
  }
}
